import React, { useMemo } from 'react';
import { Card, Row, Col } from 'react-bootstrap';
import { DataListProps } from './DataList.types';
import './DataList.css';

/**
 * Componente genérico para exibir listas de dados em formato de tabela/cards responsivos
 * 
 * @example
 * ```tsx
 * const columns: DataListColumn<Cliente>[] = [
 *   { label: 'Cliente', name: 'nome', size: 4 },
 *   { label: 'Telefone', name: 'telefone', size: 3 },
 *   { label: 'CPF', name: 'cpf', size: 3 },
 *   {
 *     label: 'Ações',
 *     size: 2,
 *     align: 'center',
 *     render: (cliente) => (
 *       <Button onClick={() => edit(cliente.id)}>Editar</Button>
 *     )
 *   }
 * ];
 * 
 * <DataList
 *   columns={columns}
 *   data={clientes}
 *   rowKey={(cliente) => cliente.id}
 * />
 * ```
 */
export function DataList<T>({
  columns,
  data,
  onRowClick,
  rowKey,
  emptyComponent,
  className = '',
  disableHoverEffects = false,
}: DataListProps<T>) {
  // Memoizar se tem handler de click para evitar recalcular
  const hasClickHandler = useMemo(() => !!onRowClick, [onRowClick]);

  // Componente de empty state
  const emptyState = useMemo(
    () =>
      emptyComponent ?? (
        <div className="text-center text-muted py-5">
          Nenhum registro encontrado
        </div>
      ),
    [emptyComponent]
  );

  if (!data.length) {
    return <>{emptyState}</>;
  }

  return (
    <Card 
      className={`border-0 shadow-sm ${disableHoverEffects ? 'no-hover-effects' : ''} ${className}`} 
      style={{ borderRadius: '16px' }}
    >
      <Card.Body className="p-0">
        {/* HEADER - Desktop Only */}
        <div className="d-none d-lg-block border-bottom px-2 py-3 bg-light">
          <Row>
            {columns.map((column, i) => (
              <Col
                key={i}
                md={column.size}
                className={`fw-bold text-${column.align ?? 'start'}`}
              >
                {column.label}
              </Col>
            ))}
          </Row>
        </div>

        {/* ROWS */}
        {data.map((item, rowIndex) => {
          const key = rowKey ? rowKey(item, rowIndex) : rowIndex;

          return (
            <div
              key={key}
              className={`border-bottom px-2 py-3 data-list-row ${hasClickHandler ? 'clickable' : ''} ${disableHoverEffects ? 'no-hover' : ''}`}
              onClick={hasClickHandler ? () => onRowClick!(item, rowIndex) : undefined}
              style={hasClickHandler ? { cursor: 'pointer' } : undefined}
              role={hasClickHandler ? 'button' : undefined}
              tabIndex={hasClickHandler ? 0 : undefined}
            >
              <Row className="align-items-center">
                {columns.map((column, colIndex) => {
                  const colClasses = [
                    `text-${column.align ?? 'start'}`,
                    column.hideOnMobile ? 'd-none d-lg-block' : '',
                    column.className || '',
                  ]
                    .filter(Boolean)
                    .join(' ');

                  return (
                    <Col
                      key={colIndex}
                      xs={12}
                      md={column.size}
                      className={colClasses}
                    >
                      {/* Label mobile - exibe o label antes do valor */}
                      {column.label && (
                        <div className="d-lg-none text-muted small fw-semibold mb-1">
                          {column.label}
                        </div>
                      )}

                      {/* Conteúdo da célula */}
                      <div className={column.hideOnMobile ? '' : 'mb-3 mb-lg-0'}>
                        {column.render
                          ? column.render(item, rowIndex)
                          : column.name
                          ? (item as any)[column.name] ?? '-'
                          : '-'}
                      </div>
                    </Col>
                  );
                })}
              </Row>
            </div>
          );
        })}
      </Card.Body>
    </Card>
  );
}
